(() => {
  let unsubscribeVariantChange;
  let unsubscribeCartUpdate;
  const bundleRefreshControllers = new WeakMap();
  let revealObserver;
  const pad = (value) => String(value).padStart(2, '0');
  const revealMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealSelector = [
    '[data-reference-module]',
    '.elovus-hero',
    '.elovus-media-bar',
    '.elovus-social-proof',
    '.elovus-feature-grid',
    '.elovus-feature-split',
    '.elovus-comparison',
    '.elovus-testimonials',
    '.elovus-guarantee',
    '.elovus-process',
    '.elovus-collection-hero',
  ].join(',');

  const getRevealObserver = () => {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    return revealObserver;
  };

  const initReveals = (scope = document) => {
    const candidates = [];
    if (scope instanceof Element && scope.matches(revealSelector)) candidates.push(scope);
    candidates.push(...scope.querySelectorAll(revealSelector));

    candidates.forEach((target, index) => {
      const main = target.closest('main[data-template], main[data-preview]');
      const isHome = !main || main.dataset.template === 'index' || main.dataset.preview === 'home';
      if (!isHome) {
        target.classList.add('is-visible');
        target.removeAttribute('data-elovus-reveal');
        target.removeAttribute('data-elovus-reveal-initialized');
        target.style.removeProperty('--elovus-reveal-delay');
        return;
      }

      target.setAttribute('data-elovus-reveal', '');
      target.style.setProperty('--elovus-reveal-delay', `${(index % 4) * 75}ms`);
      if (target.dataset.elovusRevealInitialized === 'true') return;
      target.dataset.elovusRevealInitialized = 'true';

      if (revealMotionQuery.matches || !('IntersectionObserver' in window)) {
        target.classList.add('is-visible');
        return;
      }
      getRevealObserver()?.observe(target);
    });
  };

  const initCountdown = (root) => {
    if (root.dataset.elovusInitialized === 'true') return;
    const output = root.querySelector('[data-elovus-countdown]');
    const deadline = Date.parse(root.dataset.deadline || '');
    if (!output || Number.isNaN(deadline)) return;
    root.dataset.elovusInitialized = 'true';

    const render = () => {
      const remaining = Math.max(0, deadline - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      };

      for (const [key, value] of Object.entries(values)) {
        const node = output.querySelector(`[data-${key}]`);
        if (node) node.textContent = pad(value);
      }
      output.hidden = false;
      return remaining > 0;
    };

    if (!render()) return;
    const timer = window.setInterval(() => {
      if (!render()) window.clearInterval(timer);
    }, 1000);
  };

  const syncBundleQuantity = (section, value) => {
    section.querySelectorAll('input[name="quantity"]').forEach((quantity) => {
      if (quantity.value === value) return;
      quantity.value = value;
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const syncBundlePurchaseAvailability = (section, bundle, serverHtml, sectionId) => {
    const sourceButton = section.querySelector('.product-form__submit');
    if (!sourceButton) return;

    const sourceLabel = sourceButton.querySelector('span');
    const hasAvailableQuantity = Boolean(bundle.querySelector('input[data-elovus-bundle-option]'));
    if (!hasAvailableQuantity) {
      if (sourceButton.dataset.elovusBundleDisabled !== 'true') {
        sourceButton.dataset.elovusBundlePreviousLabel = sourceLabel?.textContent.trim() || '';
      }
      sourceButton.dataset.elovusBundleDisabled = 'true';
      sourceButton.disabled = true;
      sourceButton.setAttribute('aria-disabled', 'true');
      if (sourceLabel && bundle.dataset.unavailableLabel) sourceLabel.textContent = bundle.dataset.unavailableLabel;
      return;
    }

    if (sourceButton.dataset.elovusBundleDisabled !== 'true') return;
    const serverButton = serverHtml?.getElementById(`ProductSubmitButton-${sectionId || section.dataset.section}`);
    const serverDisabled = serverButton?.disabled || serverButton?.getAttribute('aria-disabled') === 'true';
    sourceButton.disabled = Boolean(serverDisabled);
    sourceButton.setAttribute('aria-disabled', String(Boolean(serverDisabled)));

    const serverLabel = serverButton?.querySelector('span')?.textContent.trim();
    const restoredLabel = serverLabel || sourceButton.dataset.elovusBundlePreviousLabel;
    if (sourceLabel && restoredLabel) sourceLabel.textContent = restoredLabel;
    delete sourceButton.dataset.elovusBundleDisabled;
    delete sourceButton.dataset.elovusBundlePreviousLabel;
  };

  const getLiveVariantPicker = (section) => {
    const pickers = Array.from(section.querySelectorAll('variant-selects'));
    return pickers.find((picker) => (
      !picker.hidden
      && picker.style.display !== 'none'
      && picker.getAttribute('aria-hidden') !== 'true'
    )) || pickers[0] || null;
  };

  const syncBundleVariantPicker = (section, bundle) => {
    const picker = getLiveVariantPicker(section);
    const selectedInput = bundle.querySelector('input[data-elovus-bundle-option]:checked');
    const targetSlot = selectedInput?.closest('.elovus-bundle__option')
      ?.querySelector('[data-elovus-bundle-variant-slot]');
    if (!picker || !targetSlot) return;

    section.querySelectorAll('[data-elovus-bundle-variant-picker]').forEach((candidate) => {
      if (candidate !== picker) candidate.removeAttribute('data-elovus-bundle-variant-picker');
    });
    picker.setAttribute('data-elovus-bundle-variant-picker', '');
    if (!targetSlot.contains(picker)) targetSlot.append(picker);
  };

  const normalizeBundleInstance = (section, bundle, sourceSectionId) => {
    const instanceSectionId = section.dataset.section;
    if (!instanceSectionId || !sourceSectionId || instanceSectionId === sourceSectionId) return;

    [bundle, ...bundle.querySelectorAll('*')].forEach((node) => {
      Array.from(node.attributes).forEach((attribute) => {
        if (!attribute.value.includes(sourceSectionId)) return;
        node.setAttribute(attribute.name, attribute.value.replaceAll(sourceSectionId, instanceSectionId));
      });
    });
  };

  const initBundle = (bundle, serverHtml, sectionId) => {
    if (bundle.dataset.elovusInitialized === 'true') return;
    const section = bundle.closest('product-info');
    if (!section) return;
    bundle.dataset.elovusInitialized = 'true';
    syncBundlePurchaseAvailability(section, bundle, serverHtml, sectionId);

    if (section.dataset.elovusQuantitySync !== 'true') {
      section.dataset.elovusQuantitySync = 'true';
      section.addEventListener('change', (event) => {
        if (event.target.name !== 'quantity') return;
        const activeBundle = section.querySelector('[data-elovus-bundle]');
        activeBundle?.querySelectorAll('input[data-elovus-bundle-option]').forEach((option) => {
          option.checked = option.value === event.target.value;
        });
        if (activeBundle) syncBundleVariantPicker(section, activeBundle);
      });
    }

    const selected = bundle.querySelector('input[data-elovus-bundle-option]:checked');
    if (selected) syncBundleQuantity(section, selected.value);
    syncBundleVariantPicker(section, bundle);

    bundle.addEventListener('change', (event) => {
      if (!event.target.matches('input[data-elovus-bundle-option]')) return;
      syncBundleQuantity(section, event.target.value);
      syncBundleVariantPicker(section, bundle);
    });
  };

  const refreshBundle = ({ data } = {}) => {
    if (!data?.html || !data.sectionId) return;
    const sections = Array.from(document.querySelectorAll('product-info'));
    const source = data.source?.matches?.('product-info') && data.source.isConnected ? data.source : null;
    const section = source
      || sections.find((node) => node.dataset.section === data.sectionId)
      || sections.find((node) => node.dataset.originalSection === data.sectionId);
    const current = section?.querySelector('[data-elovus-bundle]');
    const replacement = data.html.querySelector('[data-elovus-bundle]');
    if (!section || !current || !replacement) return;
    normalizeBundleInstance(section, replacement, data.sectionId);

    const previousValue = current.querySelector('input[data-elovus-bundle-option]:checked')?.value;
    const preservedOption = previousValue
      ? replacement.querySelector(`input[data-elovus-bundle-option][value="${previousValue}"]`)
      : null;
    if (preservedOption) preservedOption.checked = true;

    const preservedPicker = getLiveVariantPicker(section);
    if (preservedPicker) {
      preservedPicker.setAttribute('data-elovus-bundle-variant-picker', '');
      current.before(preservedPicker);
    }
    current.replaceWith(replacement);
    initBundle(replacement, data.html, data.sectionId);
  };

  const refreshBundleFromSection = async (section) => {
    const sectionId = section.dataset.originalSection || section.dataset.section;
    const productUrl = section.dataset.url;
    const variantId = section.querySelector('input[name="id"]')?.value;
    if (!sectionId || !productUrl || !variantId || !section.querySelector('[data-elovus-bundle]')) return;

    bundleRefreshControllers.get(section)?.abort();
    const controller = new AbortController();
    bundleRefreshControllers.set(section, controller);

    try {
      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('variant', variantId);
      requestUrl.searchParams.set('section_id', sectionId);
      const response = await fetch(requestUrl, {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Bundle refresh failed with status ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      refreshBundle({ data: { html, sectionId, source: section } });
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Unable to refresh Elovus bundle availability.', error);
    } finally {
      if (bundleRefreshControllers.get(section) === controller) bundleRefreshControllers.delete(section);
    }
  };

  const refreshBundlesAfterCartUpdate = () => Promise.all(
    Array.from(document.querySelectorAll('product-info')).map(refreshBundleFromSection),
  );

  const initVariantSync = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (!unsubscribeVariantChange) {
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, refreshBundle);
    }
    if (!unsubscribeCartUpdate) {
      unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, refreshBundlesAfterCartUpdate);
    }
  };

  const initPageFaqFilters = (root) => {
    if (root.dataset.pageFaqInitialized === 'true') return;
    const buttons = Array.from(root.querySelectorAll('[data-page-faq-filter]'));
    const answers = Array.from(root.querySelectorAll('[data-faq-groups]'));
    const heading = root.querySelector('[data-page-faq-heading]');
    if (!buttons.length || !answers.length) return;
    root.dataset.pageFaqInitialized = 'true';

    const selectFilter = (button) => {
      const filter = button.dataset.pageFaqFilter;
      buttons.forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-pressed', String(active));
      });

      const visibleAnswers = answers.filter((answer) => {
        const groups = (answer.dataset.faqGroups || '').split(/\s+/);
        const visible = groups.includes(filter);
        answer.hidden = !visible;
        if (!visible) answer.open = false;
        return visible;
      });
      if (visibleAnswers[0]) visibleAnswers[0].open = true;
      if (heading) heading.textContent = `${button.textContent.trim()} questions`;
    };

    buttons.forEach((button) => button.addEventListener('click', () => selectFilter(button)));
    selectFilter(buttons.find((button) => button.classList.contains('is-active')) || buttons[0]);
  };

  const initPageAssistant = (root) => {
    if (root.dataset.pageAssistantInitialized === 'true') return;
    const tabs = Array.from(root.querySelectorAll('[data-page-assistant-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-page-assistant-panel]'));
    const message = root.querySelector('[data-page-assistant-message]');
    const reset = root.querySelector('[data-page-assistant-reset]');
    const play = root.querySelector('[data-page-assistant-play]');
    const video = root.querySelector('[data-page-assistant-avatar] video');
    const form = root.querySelector('[data-page-assistant-form]');
    if (!tabs.length || !panels.length) return;
    root.dataset.pageAssistantInitialized = 'true';

    const selectTab = (key) => {
      tabs.forEach((candidate) => {
        const active = candidate.dataset.pageAssistantTab === key;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-selected', String(active));
      });
      panels.forEach((panel) => {
        const active = panel.dataset.pageAssistantPanel === key;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };

    const setMessage = (lines) => {
      if (!message) return;
      const paragraphs = lines.map((line) => {
        const paragraph = document.createElement('p');
        paragraph.textContent = line;
        return paragraph;
      });
      message.replaceChildren(...paragraphs);
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => selectTab(tab.dataset.pageAssistantTab)));
    selectTab(tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.pageAssistantTab || 'help');

    if (play && video) {
      play.addEventListener('click', async () => {
        if (video.paused) {
          try {
            await video.play();
            play.classList.add('is-playing');
            play.setAttribute('aria-label', 'Pause assistant introduction');
          } catch {
            play.classList.remove('is-playing');
          }
        } else {
          video.pause();
          play.classList.remove('is-playing');
          play.setAttribute('aria-label', 'Play assistant introduction');
        }
      });
    } else if (play) {
      play.disabled = true;
      play.setAttribute('aria-disabled', 'true');
    }

    reset?.addEventListener('click', () => {
      video?.pause();
      if (video) video.currentTime = 0;
      play?.classList.remove('is-playing');
      selectTab('help');
      root.querySelectorAll('details[open]').forEach((details) => { details.open = false; });
      if (form) form.reset();
      setMessage([
        'Hello. I am your Elovus helper.',
        'Use the quick links below, or type a question. I am here to help you find the right answer.',
      ]);
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const question = form.elements.question?.value.trim();
      if (!question) return;
      setMessage([
        `You asked: ${question}`,
        'Choose a quick link below, or continue to the contact form for help from the team.',
      ]);
      selectTab('help');
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-elovus-promo]').forEach(initCountdown);
    scope.querySelectorAll('[data-elovus-bundle]').forEach(initBundle);
    scope.querySelectorAll('.elovus-help').forEach(initPageFaqFilters);
    scope.querySelectorAll('[data-page-assistant]').forEach(initPageAssistant);
    initReveals(scope);
    initVariantSync();
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('product-info:loaded', (event) => init(event.target));
})();
